// /* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { IconRender } from "@/components/shared/Icon-render";
import { Button } from "@/components/ui/button";
import { useDocumentDetails } from "./hook/use-document-details";
import { DocumentImage } from "./utils/document-image";
import { DocumentDetailsView } from "./utils/document-details-view";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { Card } from "@/components/ui/card";
import { cn, getSummary /* summarizeErrors */ } from "@/lib/utils";
import { ConditionRenderer } from "@/components/shared/condition-renderer";
import { DocumentSkelton } from "./utils/document-skelton";
import { DocumentProvider } from "./utils/document-provider";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertCircle,
  CircleHelp,
  Download,
  LoaderCircle,
  Save,
  // Upload,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { TooltipArrow } from "@radix-ui/react-tooltip";
import { DocumentErpLogPopup } from "./utils/document-erp-log-popup";
import { ExcelDocViewer } from "./utils/excel-doc-viewer";
import { SwitchRenderer } from "@/components/shared/switch-renderer";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { TextDocViewer } from "./utils/text-doc-viewer";
import { ReprocessDocument } from "./utils/document-reprocess";
import { DocumentAuditLogPopup } from "./utils/document-audit-log-popup";
import { useMediaQuery } from "@/hooks/use-media-query";
import { DetailsPageTopBar } from "@/components/layout/dashboard-layout/details-page-top-bar";
import { useMemo } from "react";
import { DocumentUploadDetailsForm } from "../utils/document-upload-form/document-upload-form";

export const DocumentDetails = () => {
  const {
    back,
    state,
    setState,
    searchAddress,
    updateAddress,
    updateJsonData,
    loadErpLogs,
    reprocessErp,
    reprocessDocument,
    currentCompanyData,
    workflowList,
    doc_id,
    user_id,
    company_id,
    manageAuditLogPopup,
    searchSupplier,
    updateSupplierMapping,
    reprocessErpId,
    reprocessDocId,
    domain_id,
    downloadDocument,
    searchItemMaster,
    updateItemMaster,
    mutationMasterData,
    formPopupOpen,
    setFormPopupOpen,
    formSubmitHandle,
    documentDelete,
    // documentDeleteProcess,
    documentVoid,
    documentVoidProcess,
    isVoid,
    onUpdateIsSimilarityMatch,
    originalDetailsData,
    allRetailerList,
    reprocessDocumentProcess,
    onIgnorePriceValidations,
    ignorePriceValidations,
  } = useDocumentDetails();

  const height = "h-[700px]";

  const matches = useMediaQuery("(max-width: 767px)");

  const displayValue = (prop: any) => {
    if (prop?.terminal_keys) {
      const getKey = Object.values(prop?.terminal_keys);
      if (Array.isArray(getKey)) {
        return getKey.join(", ");
      }
      if (typeof getKey === "string") {
        return getKey;
      }
      return JSON.stringify(getKey);
    }
    return "";
  };

  const status_list: any = useMemo(() => {
    return state?.documentDetailsData?.erp_status
      ? getSummary(state?.documentDetailsData?.erp_status)
      : {};
  }, [state?.documentDetailsData?.erp_status]);

  const support_multi_doc_type =
    currentCompanyData?.settings?.support_multi_doc_type;

  const showBranchId =
    currentCompanyData?.settings?.customer_mapping_settings?.show_branch_id;

  // A deleted document is view-only: only Erp Log, Download and Back stay
  // enabled (same rule as the document list)
  const isReadOnly = state?.documentDetailsData?.is_deleted === true;

  const getClasses = (k: string) => {
    if (!support_multi_doc_type || ["snc_status", "erp_status"].includes(k)) {
      return "border-gray-200 bg-gray-50 text-gray-500";
    }
    return "border-red-200 bg-red-50 text-red-500";
  };

  const pages =
    state?.documentDetailsData?.pages || state?.detailsData?.pages || [];

  // console.log({ validation_failed: status_list.validation_failed });
  const validation_failed = status_list.validation_failed;
  const is_duplicate = state?.documentDetailsData?.state === "duplicate";

  return (
    <DocumentProvider
      state={state}
      setState={setState}
      searchAddress={searchAddress}
      updateAddress={updateAddress}
      searchSupplier={searchSupplier}
      updateSupplierMapping={updateSupplierMapping}
      searchItemMaster={searchItemMaster}
      updateItemMaster={updateItemMaster}
      mutationMasterData={mutationMasterData}
      onUpdateIsSimilarityMatch={onUpdateIsSimilarityMatch}
      onIgnorePriceValidations={onIgnorePriceValidations}
      ignorePriceValidations={ignorePriceValidations}
      showBranchId={showBranchId}
      isReadOnly={isReadOnly}
    >
      <DetailsPageTopBar
        title=""
        leftSideComponent={
          <>
            {Object.entries(status_list)?.length !== 0 ? (
              <>
                <TooltipProvider delayDuration={0}>
                  {Object.entries(status_list).map(
                    ([key, keyValue]: any, index) => {
                      const letter = key.replaceAll("_", " ");
                      return (
                        <Tooltip key={index}>
                          <TooltipTrigger className="text-left !p-0">
                            <div className="capitalize">
                              <span
                                className={cn(
                                  "capitalize rounded-full text-slate-700 flex items-center bg-slate-50 justify-center px-2 text-[13px] py-1  border pr-3 ",
                                  keyValue?.length !== 0 ? getClasses(key) : "",
                                )}
                              >
                                {letter}
                              </span>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <TooltipArrow />
                            <ul className="details-list">
                              {keyValue.map(
                                (
                                  item: {
                                    type: string;
                                    message: string;
                                    data?: string[];
                                  },
                                  pIndex: number,
                                ) => {
                                  if (item.type === "string") {
                                    return <li key={pIndex}>{item.message}</li>;
                                  } else if (item.type === "array") {
                                    return (
                                      <li key={pIndex}>
                                        {item.message}
                                        <ul>
                                          {item?.data?.map(
                                            (data: string, index: number) => {
                                              return (
                                                <li key={index}>{data}</li>
                                              );
                                            },
                                          )}
                                        </ul>
                                      </li>
                                    );
                                  }
                                },
                              )}
                            </ul>
                          </TooltipContent>
                        </Tooltip>
                      );
                    },
                  )}
                </TooltipProvider>
              </>
            ) : null}

            {state?.documentDetailsData?.is_flagged?.enabled ? (
              <TooltipProvider>
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <div className="rounded-full text-slate-700 flex items-center gap-2 bg-slate-50 justify-center px-2 py-1 border">
                      <CircleHelp size={20} strokeWidth={1.5} />
                      <span className="text-sm">Flag</span>
                    </div>
                  </TooltipTrigger>
                  <ConditionRenderer
                    condition={
                      state?.documentDetailsData?.is_flagged?.enabled
                        ? true
                        : false
                    }
                  >
                    <TooltipContent>
                      <TooltipArrow offset={0} />
                      <p>
                        {displayValue(state?.documentDetailsData?.is_flagged)}
                      </p>
                    </TooltipContent>
                  </ConditionRenderer>
                </Tooltip>
              </TooltipProvider>
            ) : null}

            {/* <div className="flex items-center space-x-2 ml-2 document-header-valid-document">
              <Switch
                onCheckedChange={(prop) => documentDelete(prop)}
                id="valid_document"
                disabled={documentDeleteProcess}
                checked={state?.documentDetailsData?.is_valid ? true : false}
              />
              <Label htmlFor="valid_document">Reviewed</Label>
            </div> */}
            {is_duplicate || validation_failed /* || is_duplicate */ ? (
              <div className="flex items-center space-x-2 ml-2 document-header-valid-document">
                <Button
                  className="!px-2 !py-0.5"
                  onClick={() => {
                    if (!state?.documentDetailsData?.is_valid) {
                      documentDelete(true);
                    }
                  }}
                  disabled={state?.documentDetailsData?.is_valid || isReadOnly}
                >
                  {state?.documentDetailsData?.is_valid
                    ? "Reviewed"
                    : "Mark as reviewed"}
                </Button>
              </div>
            ) : null}

            {["on_hold", "void", "erp_failed", "snc_success"].includes(
              state?.documentDetailsData?.state,
            ) ? (
              <div className="flex items-center space-x-2 ml-2 document-header-valid-document">
                <Switch
                  id="mark_as_void"
                  checked={isVoid}
                  disabled={documentVoidProcess || isReadOnly}
                  onCheckedChange={(prop) => documentVoid(prop)}
                />
                <Label htmlFor="mark_as_void">Mark as Void</Label>
              </div>
            ) : null}
          </>
        }
      >
        <Button
          className="relative transition-all "
          onClick={reprocessErp}
          variant={"outline"}
          disabled={isVoid || isReadOnly}
        >
          Reprocess Erp
          {reprocessErpId ? (
            <span className="w-[16px] h-[16px] inline-flex items-center justify-center animate-spin ">
              <LoaderCircle className="" />
            </span>
          ) : null}
        </Button>
        <ReprocessDocument
          currentCompanyData={currentCompanyData}
          doc_id={doc_id}
          onAction={reprocessDocument}
          workflowList={workflowList}
          isReprocess={reprocessDocId ? true : false}
          allRetailerList={allRetailerList}
          selectedRetailer={state?.documentDetailsData?.retailer_id}
          disabled={isVoid || isReadOnly}
        />
        <Button onClick={loadErpLogs} variant={"outline"} className="!px-[7px]">
          Erp Log
        </Button>
        <Button
          disabled={!domain_id || isReadOnly}
          onClick={() => manageAuditLogPopup(true)}
          variant={"outline"}
          className="!px-[7px]"
        >
          Audit Log
        </Button>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              // disabled={!domain_id}
              onClick={() => downloadDocument()}
              variant={"outline"}
              className="w-[30px]"
            >
              <Download />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Download</p>
          </TooltipContent>
        </Tooltip>

        {/* {support_multi_doc_type && upload_document_allow?.create === true ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className="relative w-[30px]"
                variant={"outline"}
                onClick={() => setFormPopupOpen(true)}
              >
                <Upload name="ArrowLeft" strokeWidth={1.5} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Upload Document</p>
            </TooltipContent>
          </Tooltip>
        ) : null} */}

        <div className="flex items-center space-x-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={updateJsonData}
                variant={"outline"}
                disabled={reprocessDocumentProcess || isVoid || isReadOnly}
                className="border-green-500 w-[30px] text-green-500 hover:text-green-500"
              >
                <Save />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Save</p>
            </TooltipContent>
          </Tooltip>
        </div>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              className="relative w-[30px]"
              variant={"action"}
              onClick={() => back()}
            >
              <IconRender name="ArrowLeft" strokeWidth={1.5} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Back</p>
          </TooltipContent>
        </Tooltip>
      </DetailsPageTopBar>
      <ConditionRenderer condition={state?.skeltonLoading}>
        <DocumentSkelton className={cn(height, "mt-5")} />
        <>
          <Card className="mt-5 bg-[#fafafc]">
            <ResizablePanelGroup
              direction={matches ? "vertical" : "horizontal"}
              className="relative overflow-hidden flex gap-2 resizable-panels"
            >
              <ResizablePanel defaultSize={50}>
                <ConditionRenderer
                  condition={state?.documentError ? true : false}
                >
                  <div className="p-5">
                    <Alert variant="destructive" className="p-5">
                      <AlertCircle className="h-4 w-4" />
                      <AlertTitle>Error</AlertTitle>
                      <AlertDescription>
                        {state?.documentError?.status?.message ||
                          "Something went wrong"}
                      </AlertDescription>
                    </Alert>
                  </div>
                  <>
                    <SwitchRenderer
                      type={state?.fileType}
                      components={{
                        xlsx: (
                          <ExcelDocViewer
                            fileType={state.fileType}
                            filePath={state.filePath}
                            height={height}
                          />
                        ),
                        xls: (
                          <ExcelDocViewer
                            fileType={state.fileType}
                            filePath={state.filePath}
                            height={height}
                          />
                        ),
                        text: (
                          <TextDocViewer
                            filePath={state.filePath}
                            className={cn(height)}
                          />
                        ),
                        default: (
                          <DocumentImage className={cn(height)} data={pages} />
                        ),
                      }}
                    />
                  </>
                </ConditionRenderer>
              </ResizablePanel>
              <ResizableHandle withHandle />
              <ResizablePanel>
                <ConditionRenderer
                  condition={state?.domainError ? true : false}
                >
                  <div className="p-5">
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertTitle>Error</AlertTitle>
                      <AlertDescription>
                        {state?.domainError?.status?.message ||
                          "Something went wrong"}
                      </AlertDescription>
                    </Alert>
                  </div>
                  <DocumentDetailsView
                    className={cn("overflow-y-auto", height)}
                    detailsData={state?.detailsData}
                    updateJsonData={updateJsonData}
                    originalDetailsData={originalDetailsData}
                  />
                </ConditionRenderer>
              </ResizablePanel>
            </ResizablePanelGroup>
          </Card>
        </>
      </ConditionRenderer>
      <ConditionRenderer condition={state.erpLogPopup}>
        <DocumentErpLogPopup
          data={state.erpLogData}
          open={state.erpLogPopup}
          setOpen={() => setState({ type: "close-erp-log", data: null })}
        />
      </ConditionRenderer>
      <ConditionRenderer condition={state.auditLogPopup}>
        <DocumentAuditLogPopup
          open={state.auditLogPopup}
          setOpen={() => manageAuditLogPopup(false)}
          doc_id={doc_id}
          company_id={company_id}
          user_id={user_id}
          domain_id={domain_id}
        />
      </ConditionRenderer>
      <ConditionRenderer condition={formPopupOpen}>
        <DocumentUploadDetailsForm
          formData={{}}
          onOpenChange={setFormPopupOpen}
          onFormSubmit={formSubmitHandle}
          open={formPopupOpen}
          workflowList={workflowList}
          currentCompanyData={currentCompanyData}
        />
      </ConditionRenderer>
    </DocumentProvider>
  );
};
